import React, { useEffect, useState, useRef } from 'react';
import { getSettings, updateSettings, uploadLogo, removeLogo } from '../../api';

const PALETTE_PRESETS = [
  { name: 'Indigo (Default)', primary: '#4f46e5', accent: '#06b6d4', sidebar: 'var(--text)' },
  { name: 'Blue', primary: '#2563eb', accent: '#0ea5e9', sidebar: '#1e3a5f' },
  { name: 'Emerald', primary: '#059669', accent: '#10b981', sidebar: '#064e3b' },
  { name: 'Rose', primary: '#e11d48', accent: '#f43f5e', sidebar: '#4c0519' },
  { name: 'Violet', primary: '#7c3aed', accent: '#8b5cf6', sidebar: '#2e1065' },
  { name: 'Orange', primary: '#ea580c', accent: '#f97316', sidebar: '#431407' },
  { name: 'Slate', primary: '#475569', accent: 'var(--muted)', sidebar: '#0f172a' },
];

export default function BrandSettings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getSettings().then(data => {
      setS(data);
      const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      if (data.company_logo) setLogoPreview(`${base}${data.company_logo}`);
    });
  }, []);

  const handleSave = async () => {
    await updateSettings({
      company_name: s.company_name,
      primary_color: s.primary_color,
      accent_color: s.accent_color,
      sidebar_color: s.sidebar_color,
    });
    // Apply branding immediately
    document.documentElement.style.setProperty('--primary', s.primary_color);
    document.documentElement.style.setProperty('--accent', s.accent_color);
    document.documentElement.style.setProperty('--sidebar-bg', s.sidebar_color);
    window.__brandingUpdated?.();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    const { url } = await uploadLogo(fd);
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setLogoPreview(`${base}${url}`);
    window.__brandingUpdated?.();
  };

  const handleRemoveLogo = async () => {
    await removeLogo();
    setLogoPreview(null);
    window.__brandingUpdated?.();
  };

  const applyPreset = (preset) => {
    setS(prev => ({ ...prev, primary_color: preset.primary, accent_color: preset.accent, sidebar_color: preset.sidebar }));
  };

  if (!s) return <div className="empty">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Rebrand Settings</div>
          <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: 2 }}>Customize your organisation's branding</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: name & logo */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 20 }}>Organisation Info</div>

          <div className="form-group">
            <label>Company Name</label>
            <input value={s.company_name || ''} onChange={e => setS(p => ({ ...p, company_name: e.target.value }))} placeholder="Your company name" />
          </div>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>Company Logo</div>
          <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 12 }}>You can replace the HRTrack logo by uploading your company logo.</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
            {logoPreview ? (
              <img src={logoPreview} alt="logo" style={{ width: 80, height: 80, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--hairline)', padding: 4 }} />
            ) : (
              <div style={{ width: 80, height: 80, border: '2px dashed var(--border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '0.75rem' }}>No logo</div>
            )}
            <div>
              <button type="button" className="btn btn-outline" onClick={() => fileRef.current.click()} style={{ color: 'var(--primary)', marginBottom: 8, display: 'block' }}>
                Upload your company logo
              </button>
              {logoPreview && (
                <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.875rem' }} onClick={handleRemoveLogo}>
                  Remove logo
                </button>
              )}
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleLogoUpload} />
            </div>
          </div>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>Company logo should be in the format of jpg or png.</div>
        </div>

        {/* Right: color palette */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 20 }}>Color Palette</div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>Presets</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {PALETTE_PRESETS.map(preset => (
                <button key={preset.name} type="button" onClick={() => applyPreset(preset)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: `2px solid ${s.primary_color === preset.primary ? preset.primary : 'var(--border)'}`, borderRadius: 20, cursor: 'pointer', background: s.primary_color === preset.primary ? '#f0f4ff' : 'transparent', fontSize: '0.8rem' }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: preset.primary, flexShrink: 0 }} />
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: preset.accent, flexShrink: 0 }} />
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: preset.sidebar, flexShrink: 0 }} />
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.5px' }}>Custom Colors</div>
            {[
              { label: 'Primary Color', key: 'primary_color', desc: 'Buttons, active states, links' },
              { label: 'Accent Color', key: 'accent_color', desc: 'Highlights and secondary elements' },
              { label: 'Sidebar Color', key: 'sidebar_color', desc: 'Navigation sidebar background' },
            ].map(c => (
              <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <input type="color" value={s[c.key] || '#000000'} onChange={e => setS(p => ({ ...p, [c.key]: e.target.value }))}
                  style={{ width: 44, height: 44, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{c.label}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>{c.desc}</div>
                </div>
                <div style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--muted)' }}>{s[c.key]}</div>
              </div>
            ))}
          </div>

          {/* Live preview strip */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 10 }}>Preview</div>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', height: 48 }}>
              <div style={{ width: 120, background: s.sidebar_color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>Sidebar</div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)' }}>
                <div style={{ width: 60, height: 24, background: s.primary_color, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem' }}>Button</div>
                <div style={{ width: 60, height: 24, background: s.accent_color, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem' }}>Accent</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button className="btn btn-primary" onClick={handleSave} style={{ padding: '10px 28px' }}>Save Organisation Info</button>
        {saved && <span className="badge badge-green">✓ Saved successfully</span>}
      </div>
    </div>
  );
}
